import { useTenantAvailable, useTimeZone } from '@api/application-configuration';
import { Save } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Form } from '@unistocks/components/form/form';
import { requiredSchema } from '@unistocks/components/form/validators';
import { InputRef } from '@unistocks/components/basic/input';
import { Page } from '@unistocks/components/page/page';
import { toast } from '@unistocks/components/toast';
import { TypeaheadRef } from '@unistocks/components/typeahead/typeahead';
import { t } from '@unistocks/localization';
import { UpdateProfileDto } from '@unistocks/proxy/axios-client';
import {
  setProfileGetData,
  useProfileGetQuery,
  useProfileUpdateMutation
} from '@unistocks/proxy/axios-client/ProfileQuery';
import {
  timeZoneSettingsGetQueryKey,
  useTimeZoneSettingsUpdateMutationWithParameters
} from '@unistocks/proxy/axios-client/TimeZoneSettingsQuery';
import { APP_CONFIG_QUERY_KEY } from '@unistocks/shared/hooks/use-application-configuration';
import { useTimeZones } from '@unistocks/shared/localization/time-zone';
import { SelectOption } from '@unistocks/types';
import { Href, useRouter } from 'expo-router';
import { useRef } from 'react';
import { z } from 'zod';

type ProfileFormValues = Partial<UpdateProfileDto> & { timeZone?: string };

const timeZoneKeyExtractor = (option: SelectOption) => option.value;
const timeZoneFilter = (searchText: string, options: ReadonlyArray<SelectOption>) =>
  options.filter(option => option.key.toLowerCase().includes(searchText.toLowerCase()));

const schemaFactory = () =>
  z.object({
    name: requiredSchema(z.string(), t('AbpAccount::DisplayName:Name')),
    surname: z.string().nullish(),
    email: requiredSchema(z.string(), t('AbpAccount::DisplayName:Email')),
    timeZone: requiredSchema(z.string(), t('AbpTiming::DisplayName:Abp.Timing.Timezone'))
  });

export const EditProfile: React.FC = () => {
  const isTenantUser = useTenantAvailable();
  const queryClient = useQueryClient();
  const router = useRouter();
  const schema = schemaFactory();

  const settingsHref: Href = isTenantUser ? '/(tabs)/(menu)/settings' : '/(admin)/(menu)/settings';

  const firstNameRef = useRef<InputRef>(null);
  const lastNameRef = useRef<InputRef>(null);
  const emailRef = useRef<InputRef>(null);
  const timeZoneRef = useRef<TypeaheadRef>(null);

  const { data: timeZoneOptions, isPending: isLoadingTimeZones } = useTimeZones();
  const timeZone = useTimeZone();

  const { data: profile } = useProfileGetQuery();

  const initialValues: ProfileFormValues = {
    name: profile?.name,
    surname: profile?.surname,
    email: profile?.email,
    timeZone
  };

  const { mutateAsync: updateProfile } = useProfileUpdateMutation({
    onSuccess: res => {
      toast.success(t('AbpAccount::PersonalSettingsSaved'));

      setProfileGetData(queryClient, () => res);

      router.back();
    }
  });

  const { mutateAsync: updateTimeZone } = useTimeZoneSettingsUpdateMutationWithParameters({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: APP_CONFIG_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: timeZoneSettingsGetQueryKey() });
    }
  });

  const timeZoneLabelExtractor = (value: string) => {
    const option = timeZoneOptions?.find(option => option.value === value);
    return option ? option.key : value;
  };

  const submit = ({ timeZone, ...values }: typeof initialValues) => {
    const promises = [];

    if (timeZone !== initialValues.timeZone) {
      promises.push(updateTimeZone({ timezone: timeZone }));
    }

    promises.push(
      updateProfile({
        ...profile,
        ...values
      } as UpdateProfileDto)
    );

    return Promise.all(promises);
  };

  return (
    <Page size={'small'}>
      <Page.ScrollView
        headerTitle={t('AbpAccount::PersonalSettings')}
        headerLogo={false}
        breadcrumbs={[
          {
            key: 'settings',
            href: settingsHref,
            name: t('::Menu:Settings'),
            rootPathName: 'settings',
            Icon: () => null
          }
        ]}
      >
        <Page.Content>
          <Form schema={schema} initialValues={initialValues} onSubmit={submit} triggerIcon={Save}>
            <Form.Input
              ref={firstNameRef}
              isRequired
              name={'name'}
              label={t('AbpAccount::DisplayName:Name')}
              placeholder={t('AbpAccount::DisplayName:Name')}
              returnKeyType="next"
              onSubmitEditing={() => lastNameRef.current?.focus()}
            />

            <Form.Input
              ref={lastNameRef}
              name={'surname'}
              label={t('AbpAccount::DisplayName:Surname')}
              placeholder={t('AbpAccount::DisplayName:Surname')}
              returnKeyType="next"
              onSubmitEditing={() => emailRef.current?.focus()}
            />

            <Form.Input
              ref={emailRef}
              isRequired
              name={'email'}
              label={t('AbpAccount::DisplayName:Email')}
              placeholder={t('AbpAccount::DisplayName:Email')}
              returnKeyType="next"
              keyboardType={'email-address'}
              onSubmitEditing={() => timeZoneRef.current?.open()}
            />

            <Form.Typeahead<string>
              ref={timeZoneRef}
              name={'timeZone'}
              isRequired
              isDisabled={isLoadingTimeZones}
              label={t('AbpTiming::DisplayName:Abp.Timing.Timezone')}
              placeholder={t('AbpTiming::DisplayName:Abp.Timing.Timezone')}
              options={timeZoneOptions}
              keyExtractor={timeZoneKeyExtractor}
              valueFormatter={timeZoneLabelExtractor}
              filter={timeZoneFilter}
            />
          </Form>
        </Page.Content>
      </Page.ScrollView>
    </Page>
  );
};

EditProfile.displayName = 'MyProfileScreen';
